/**
 * AppSplash — 앱을 열면 가장 먼저 나오는 **동적 스플래시** (2026-08-27 전면 교체).
 *
 * 전에는 템플릿(픽쿠리) 시절의 노란 배경 + 통통 튀는 로고였다. 지금은 Peeka
 * 번개가 그려지고 터진 뒤 워드마크가 맺히는 **1.6초짜리 영상**을 튼다.
 *
 * ── 시간 예산: 전체 2초를 넘지 않는다 ─────────────────────────────────
 *   0ms      마운트 · 영상 재생 시작 · 네이티브 스플래시 즉시 hide
 *   ~1612ms  영상 끝(onEnded) → 페이드 시작
 *   +200ms   페이드 끝 → 사라짐  (합 ≈ 1.81초)
 *
 *   1700ms   **하드 타임아웃** — 디코딩이 늦거나 onEnded 가 안 와도 여기서
 *            무조건 페이드를 시작한다. 최악이 1.9초 + 타이머 오차라 2초 안이다.
 *   900ms    최소 노출 — 영상이 곧바로 실패해도 이만큼은 보여 준다.
 *            0.2초 만에 사라지면 '깜빡였다'로 읽혀 없느니만 못하다.
 *
 * ── 못 트는 환경에서 ────────────────────────────────────────────────
 * 영상이 실패하면(코덱 없음 · 자동재생 거부 · 저전력 모드) **폴백 애니메이션**으로
 * 넘어간다 — 같은 배경 위에서 워드마크가 떠오르는 CSS 키프레임이다. 번들에
 * 들어 있는 PNG 하나라 네트워크도, 디코더도 필요 없다.
 * 그것마저 못 그리면(이미지 로드 실패) 기다리지 않고 바로 본 화면으로 간다.
 *
 * ── 이어 붙는 자리 ──────────────────────────────────────────────────
 * 영상의 마지막 프레임 바탕은 #070B18, 앱의 페이지 배경은 #0E0F1A 다. 거의 같은
 * 색이라 페이드가 끝나는 순간 화면이 튀지 않는다. 스플래시가 떠 있는 동안
 * body 배경도 같은 색으로 눌러 둔다 — 네이티브 스플래시가 사라진 직후 흰
 * 프레임이 한 장 스치는 것을 막는다.
 *
 * ⚠ **웹에서는 뜨지 않는다** — 네이티브 앱에서만이다(shouldShowInitialSplash).
 * ⚠ 본 화면은 이 뒤에서 **이미 그려지고 있다.** 스플래시는 초기화를 막지 않는다.
 * ⚠ 영상은 번들 자산이다(import). 원격 URL 이면 비행기 모드에서 스플래시가
 *   깨진다.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import splashVideoUrl from "../../assets/splash/peeka-splash.mp4";
import wordmarkUrl from "../../assets/brand/peeka-wordmark-white.png";
import { hideSplash, isNative } from "../../native";
import styles from "./AppSplash.module.css";

/** 전체 노출 상한 — 페이드까지 포함해 이 시간을 넘지 않는다 */
const SPLASH_MAX_MS = 2000;
/** 사라지는 데 쓰는 시간 (상한 안에 포함된다) */
const FADE_MS = 200;
/**
 * 하드 타임아웃 — 여기서 페이드를 시작한다.
 * 1700 + 200 = 1900ms. 상한(2000)까지 100ms 를 남겨 두는 것은 **타이머 오차 몫**이다.
 * 정확히 1800 으로 잡으면 이론상 딱 2000 이지만, 실제로는 스케줄러가 몇십 ms 를
 * 더 먹어 상한을 넘긴다 (브라우저 실측 1856ms — 영상이 정상으로 끝난 경우).
 */
const FADE_START_CAP_MS = 1700;
/** 최소 노출 — 이보다 빨리 사라지면 애니메이션이 아니라 깜빡임이다 */
const MIN_VISIBLE_MS = 900;
/** 한 번 보여 준 세션인지 표시 (탭·앱 세션 단위) */
const SPLASH_SESSION_KEY = "peeka:appSplashShown";

type Phase = "playing" | "fading" | "done";

export default function AppSplash() {
  const [visible] = useState(shouldShowInitialSplash);
  const [phase, setPhase] = useState<Phase>("playing");
  /** 영상이 실패해 폴백 애니메이션으로 넘어갔는가 */
  const [useFallback, setUseFallback] = useState(false);
  const mountedAt = useRef(Date.now());
  const timers = useRef<number[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  /** 끝내기가 이미 예약됐는지 — 영상 끝·하드 타임아웃·실패가 겹쳐 불릴 수 있다.
      ⚠ state 가 아니라 ref 다. 상태 갱신 함수 안에서 타이머를 걸면 React 가
        그 함수를 건너뛰거나 두 번 부를 수 있어 예약이 유실된다. */
  const finishing = useRef(false);

  /** 남은 최소 노출 시간을 지킨 뒤 페이드 → 제거 */
  const finish = useCallback(() => {
    if (finishing.current) return;
    finishing.current = true;

    const elapsed = Date.now() - mountedAt.current;
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
    timers.current.push(
      window.setTimeout(() => {
        setPhase("fading");
        timers.current.push(
          window.setTimeout(() => {
            setPhase("done");
            /* 예산을 지켰는지 개발 빌드에서 눈으로 확인할 수 있게 남긴다.
               2000ms 를 넘는 값이 찍히면 하드 타임아웃이 안 걸린 것이다. */
            if (import.meta.env.DEV) {
              console.info(
                `[splash] 전체 노출 ${Date.now() - mountedAt.current}ms (상한 ${SPLASH_MAX_MS}ms)`,
              );
            }
          }, FADE_MS),
        );
      }, wait),
    );
  }, []);

  /* 스플래시가 떠 있는 동안 body 를 같은 색으로 — 첫 paint 전에 적용해야 해서
     useLayoutEffect 다. 네이티브 스플래시가 사라진 직후의 흰 프레임 방지. */
  useLayoutEffect(() => {
    if (!visible || phase === "done") return;
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#070B18";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, [visible, phase]);

  useEffect(() => {
    /* 네이티브 스플래시(PNG)는 첫 paint 직후 바로 내린다. 스플래시를 건너뛰는
       세션에서도 마찬가지다 — 안 내리면 PNG 가 남아 앱이 멈춘 것처럼 보인다. */
    if (isNative()) {
      requestAnimationFrame(() => {
        void hideSplash();
      });
    }
    if (!visible) return;

    /* 하드 타임아웃 — onEnded 가 안 오는 경우(디코딩 지연·이벤트 유실)의 방어 */
    const cap = window.setTimeout(finish, FADE_START_CAP_MS);
    timers.current.push(cap);

    /* 자동재생이 거부되면(정책·저전력 모드) 예외가 아니라 rejected promise 로 온다 */
    const video = videoRef.current;
    if (video) {
      const played = video.play();
      if (played && typeof played.catch === "function") {
        played.catch(() => setUseFallback(true));
      }
    }

    const list = timers.current;
    return () => {
      list.forEach(window.clearTimeout);
      list.length = 0;
    };
  }, [visible, finish]);

  if (!visible || phase === "done") return null;

  return (
    <div
      aria-hidden="true"
      className={`${styles.overlay} ${phase === "fading" ? styles.fading : ""}`}
      style={{ ["--splash-fade-ms" as string]: `${FADE_MS}ms` }}
    >
      {useFallback ? (
        /* 폴백 — 영상 없이 워드마크만 떠오른다. 이미지 하나라 실패할 여지가 적고,
           그마저 실패하면 기다리지 않고 끝낸다. */
        <img
          className={styles.fallback}
          src={wordmarkUrl}
          alt=""
          onError={finish}
          onLoad={() => {
            timers.current.push(window.setTimeout(finish, MIN_VISIBLE_MS));
          }}
        />
      ) : (
        <video
          ref={videoRef}
          className={styles.video}
          src={splashVideoUrl}
          /* muted + playsInline 이 자동재생의 전제다 — 하나라도 빠지면 iOS·모바일
             크롬이 재생을 막는다. 소리는 트랙 자체가 없다. */
          autoPlay
          muted
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          onEnded={finish}
          onError={() => setUseFallback(true)}
          /* 디코딩이 시작조차 못 하는 기기에서는 stalled 가 먼저 온다 */
          onStalled={() => setUseFallback(true)}
        />
      )}
    </div>
  );
}

/**
 * 이 세션에서 이미 보여 줬는가.
 *
 * 스플래시는 '앱을 처음 여는 순간'의 장치다. 화면을 오갈 때마다 다시 트면
 * 브랜드가 아니라 장애물이 된다. 세션 저장소를 못 쓰는 환경에서는 보여 주는
 * 쪽을 고른다 — 안 나오는 것보다 한 번 더 나오는 편이 덜 이상하다.
 */
function shouldShowInitialSplash(): boolean {
  /* 웹에서는 띄우지 않는다 (2026-09-15 결정) — peeka.app(앱)·app.peeka.app(앱 안내)
     둘 다 브라우저로 여는 **사이트**다. 앱을 켜는 것과 달리 준비할 네이티브 자원이
     없어 2초짜리 영상이 순수한 대기 시간이고, 링크로 들어온 사람에게는 내용 대신
     검은 화면이 먼저 보인다. 네이티브 스플래시(PNG)를 덮어 가리는 역할도 웹에는 없다.
     ⚠ 앱/OTA 는 isNative()===true 라 종전 그대로다. */
  if (!isNative()) return false;

  try {
    if (sessionStorage.getItem(SPLASH_SESSION_KEY) === "1") return false;
    sessionStorage.setItem(SPLASH_SESSION_KEY, "1");
  } catch {
    return true;
  }
  return true;
}
